<template>
  <div
    :class="computedClasses"
   
    :aria-invalid="isInvalid"
    @focusout="touchFields"
  >
    <div v-if="hasLabel || description">
      <label
        v-if="hasLabel"
        :for="labelFor"
        :class="[
          'flex font-semibold gap-2 items-baseline',
          {
            'cursor-pointer': labelFor,
          },
        ]"
      >
        <slot name="label">
          {{ label }}
        </slot>
        <span
          v-if="isOptional"
          class="text-text-muted text-sm lowercase font-normal"
        >({{ t('optional') }})</span>
      </label>
    </div>

    <div :class="{ 'space-y-2': !props.inline }">
      <slot />

      <div
        v-if="hasFooter"
        class="flex items-baseline gap-4"
      >
        <div
          v-if="hasMessages"
          class="min-w-0 flex-1 wrap-anywhere"
        >
          <span
            v-if="description && !isInvalid"
            tabindex="-1"
            class="block text-text-muted text-sm/4"
          >
            {{ description }}
          </span>

          <TInvalidFeedback
            v-if="invalidFeedback || $slots['invalid-feedback']"
            :is-invalid="isInvalid"
            :aria-live="feedbackAriaLive"
          >
            <slot name="invalid-feedback">
              {{ invalidFeedback }}
            </slot>
          </TInvalidFeedback>
        </div>

        <p
          v-if="showsCounter"
         
          class="ml-auto shrink-0 whitespace-nowrap text-xs"
          :class="isOverMax ? 'text-text-danger' : 'text-text-muted'"
        >
          <span class="sr-only">{{ accessibleLabel }}</span>
          <span aria-hidden="true">{{ visibleCount }}</span>
        </p>
      </div>

      <slot name="append" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { INJECTION_KEYS } from '~/constants/injections'
import type { FormGroupAriaLiveOptions, FormGroupField } from './types'

const props = withDefaults(defineProps<{
  label?: string
  labelFor?: string
  isInvalid?: boolean
  isOptional?: boolean
  invalidFeedback?: string
  description?: string
  feedbackAriaLive?: FormGroupAriaLiveOptions
  inline?: boolean
  disabled?: boolean
  readonly?: boolean
  field?: FormGroupField | FormGroupField[]
  disableTouchOnBlur?: boolean
  minLength?: number
  maxLength?: number
}>(), {
  feedbackAriaLive: 'assertive',
})

const { t, locale } = useI18n()

const fields = computed(() => [props.field].flat().filter(Boolean))
const isInvalid = computed(() => fields.value.length ? fields.value.some((f) => f?.$error) : props.isInvalid)
const invalidFeedback = computed(() => fields.value.find((f) => f?.$error)?.$firstError ?? props.invalidFeedback)

const count = computed(() => {
  const value = fields.value.find((field) => typeof field?.$value === 'string')?.$value

  return typeof value === 'string' ? value.length : 0
})

const charactersShort = computed(() => {
  if (props.minLength == null || count.value >= props.minLength) {
    return null
  }

  return props.minLength - count.value
})

const showsFraction = computed(() => props.maxLength != null && charactersShort.value == null)
const showsCounter = computed(() => charactersShort.value != null || showsFraction.value)

const isOverMax = computed(() => showsFraction.value && props.maxLength != null && count.value > props.maxLength)

const formattedCount = computed(() => formatThousands(count.value, locale.value))
const formattedLimit = computed(() => props.maxLength == null ? '' : formatThousands(props.maxLength, locale.value))
const formattedShort = computed(() => charactersShort.value == null ? '' : formatThousands(charactersShort.value, locale.value))

const visibleCount = computed(() => {
  if (charactersShort.value != null) {
    return t('characterCountMore', { count: formattedShort.value })
  }

  return `${formattedCount.value} / ${formattedLimit.value}`
})

const accessibleLabel = computed(() => {
  if (charactersShort.value != null) {
    return t('characterCountMoreLabel', {
      count: formattedShort.value,
    }, charactersShort.value)
  }

  if (!showsFraction.value) {
    return ''
  }

  return t('characterCount', {
    count: formattedCount.value,
    limit: formattedLimit.value,
  }, props.maxLength ?? 0)
})

provide(INJECTION_KEYS.inputGroupDisabled, toRef(props, 'disabled'))
provide(INJECTION_KEYS.inputGroupReadonly, toRef(props, 'readonly'))
provide(INJECTION_KEYS.inputGroupInvalid, isInvalid)

const computedClasses = computed(() => {
  return [
    {
      'space-y-1': !props.inline,
      'flex items-start gap-4': props.inline,
    },
  ]
})

const slots = useSlots()
const hasLabel = computed(() => props.label || slots.label)
const hasMessages = computed(() => Boolean(props.description) || Boolean(invalidFeedback.value) || Boolean(slots['invalid-feedback']))
const hasFooter = computed(() => hasMessages.value || showsCounter.value)

function touchFields(event: FocusEvent) {
  const group = event.currentTarget as HTMLElement

  if (props.disableTouchOnBlur || group.contains(event.relatedTarget as Nullable<Node>)) {
    return
  }

  fields.value.forEach((f) => f?.$touch())
}
</script>
